// frontend/src/components/admin/SystemAudit.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import { 
  Activity, 
  Search, 
  Filter, 
  Calendar,
  User,
  Database,
  Eye,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

/**
 * SystemAudit Component
 * View preference audit logs with filtering and pagination
 */
const SystemAudit = ({ userRole }) => {
  const { t } = useTranslation();
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    has_more: false
  });
  
  // Filters
  const [filters, setFilters] = useState({
    user_id: '',
    preference_key: '',
    table_name: 'all',
    change_type: 'all',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchAuditLog();
  }, [pagination.offset, pagination.limit]);

  const fetchAuditLog = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString()
      });
      
      // Add filters to params
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      const entityId = localStorage.getItem('entityId');
      const response = await axios.get(`/api/admin/preferences/${entityId}/audit?${params}`);
      
      if (response.data.success) {
        setAuditLog(response.data.data.audit_log);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching audit log:', error);
      toast.error(t('errorFetchingAuditLog'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchAuditLog();
  };

  const clearFilters = () => {
    setFilters({
      user_id: '',
      preference_key: '',
      table_name: 'all',
      change_type: 'all',
      start_date: '',
      end_date: ''
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const nextPage = () => {
    if (pagination.has_more) {
      setPagination(prev => ({ 
        ...prev, 
        offset: prev.offset + prev.limit 
      }));
    }
  };

  const prevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ 
        ...prev, 
        offset: Math.max(0, prev.offset - prev.limit)
      }));
    }
  };

  const formatChangeType = (changeType) => {
    const types = {
      'create': { label: t('created'), color: 'bg-green-100 text-green-800' },
      'update': { label: t('updated'), color: 'bg-blue-100 text-blue-800' },
      'delete': { label: t('deleted'), color: 'bg-red-100 text-red-800' },
      'bulk_reset': { label: t('bulkReset'), color: 'bg-purple-100 text-purple-800' }
    };
    
    return types[changeType] || { label: changeType, color: 'bg-gray-100 text-gray-800' };
  };

  const formatTableName = (tableName) => {
    const tables = {
      'user_preferences': t('userPreferences'),
      'user_entity_preferences': t('userEntityPreferences'),
      'entity_preferences': t('entitySettings'),
      'preference_definitions': t('preferenceDefinitions')
    };
    
    return tables[tableName] || tableName;
  };

  const truncateValue = (value, maxLength = 50) => {
    if (!value || typeof value !== 'string') return value;
    return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          <Activity className="inline w-5 h-5 mr-2" />
          {t('systemAuditLog')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t('viewPreferenceChangeHistory')}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <input
            type="text"
            placeholder={t('userId')}
            value={filters.user_id}
            onChange={(e) => handleFilterChange('user_id', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          
          <input
            type="text"
            placeholder={t('preferenceKey')}
            value={filters.preference_key}
            onChange={(e) => handleFilterChange('preference_key', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          
          <select
            value={filters.table_name}
            onChange={(e) => handleFilterChange('table_name', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="all">{t('allTables')}</option>
            <option value="user_preferences">{t('userPreferences')}</option>
            <option value="user_entity_preferences">{t('userEntityPreferences')}</option>
            <option value="entity_preferences">{t('entitySettings')}</option>
            <option value="preference_definitions">{t('preferenceDefinitions')}</option>
          </select>
          
          <select
            value={filters.change_type}
            onChange={(e) => handleFilterChange('change_type', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="all">{t('allChanges')}</option>
            <option value="create">{t('create')}</option>
            <option value="update">{t('update')}</option>
            <option value="delete">{t('delete')}</option>
            <option value="bulk_reset">{t('bulkReset')}</option>
          </select>
          
          <input
            type="date"
            placeholder={t('startDate')}
            value={filters.start_date}
            onChange={(e) => handleFilterChange('start_date', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          
          <input
            type="date"
            placeholder={t('endDate')}
            value={filters.end_date}
            onChange={(e) => handleFilterChange('end_date', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={applyFilters}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Search className="w-4 h-4 mr-2" />
            {t('applyFilters')}
          </button>
          
          <button
            onClick={clearFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
          >
            {t('clearFilters')}
          </button>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white dark:bg-gray-900 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('timestamp')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('table')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('preference')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('changeType')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('changes')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('target')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : auditLog.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    {t('noAuditLogsFound')}
                  </td>
                </tr>
              ) : (
                auditLog.map((log) => {
                  const changeType = formatChangeType(log.change_type);
                  return (
                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(log.changed_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {log.username || 'System'}
                        </div>
                        {log.first_name && log.last_name && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {log.first_name} {log.last_name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatTableName(log.table_name)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs">
                          {log.preference_key}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${changeType.color}`}>
                          {changeType.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        <div className="space-y-1">
                          {log.old_value && (
                            <div className="text-red-600 dark:text-red-400">
                              <span className="font-medium">Old:</span> {truncateValue(log.old_value)}
                            </div>
                          )}
                          {log.new_value && (
                            <div className="text-green-600 dark:text-green-400">
                              <span className="font-medium">New:</span> {truncateValue(log.new_value)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {log.affected_target || 'N/A'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white dark:bg-gray-900 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={prevPage}
                disabled={pagination.offset === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('previous')}
              </button>
              <button
                onClick={nextPage}
                disabled={!pagination.has_more}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('showingResults', {
                    start: pagination.offset + 1,
                    end: Math.min(pagination.offset + pagination.limit, pagination.total),
                    total: pagination.total
                  })}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={prevPage}
                    disabled={pagination.offset === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={nextPage}
                    disabled={!pagination.has_more}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemAudit;